import * as yup from 'yup';
import { FC, useEffect, useRef, useState } from 'react';
import { Flex, Space, Text } from '@mantine/core';
import { useForm, yupResolver } from '@mantine/form';
import { TextInput, Checkbox, Link, PasswordInput } from '@common/components';
import { AuthFormHoc } from 'src/hoc';
import { useTranslation } from 'react-i18next';
import { IPersonalInfoValues } from '@common/types/types';
import { PATHS } from 'src/constants';
import { validationPattern } from '@common/helpers';
import { useFormFocus } from '@common/hooks';
import { PRIMARY_ACCOUNT_TYPE } from '@common/constants/primaryAccountType';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';

const initialValues: IPersonalInfoValues = {
  firstName: '',
  lastName: '',
  email: '',
  termsOfService: false,
  emailService: false,
  password: '',
  confirmPassword: '',
};

const validationFormSchema = yup.object().shape({
  firstName: validationPattern.firstName,
  lastName: validationPattern.lastName,
  email: validationPattern.email,
  termsOfService: validationPattern.termsOfService,
  password: validationPattern.password,
  confirmPassword: validationPattern.confirmPassWord,
});

const PersonalInfoForm: FC = (): JSX.Element => {
  const navigate = useNavigate();
  const [t] = useTranslation('auth');
  const form = useForm({
    initialValues: initialValues,
    validate: yupResolver(validationFormSchema),
  });
  const { setNeedFocus } = useFormFocus({ formErrors: form.errors });
  const [systemError, setSystemError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState<boolean>();
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (systemError) {
      errorRef.current?.setAttribute('tabindex', '0');
      errorRef.current?.focus();
    }
  }, [systemError]);

  const onSubmitHandler = async (values: IPersonalInfoValues): Promise<void> => {
    setIsLoading(true);
    setNeedFocus(true);
    setSystemError(null);

    try {
      await Auth.signUp({
        username: values.email,
        password: values.password,
        attributes: {
          email: values.email,
          given_name: values.firstName,
          family_name: values.lastName,
          'custom:primaryAccountType': PRIMARY_ACCOUNT_TYPE.WEBSITE_OWNER,
          // 'custom:emailNotification': `${values.emailService}`,
          'custom:emailNotification': `false`,
        },
      });
      navigate(PATHS.SEND_VERIFY_LINK, {
        state: {
          email: values.email,
          password: values.password,
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        setSystemError(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onErrorHandler = (): void => {
    setNeedFocus(true);
  };

  return (
    <AuthFormHoc
      isSignUp = {true}
      title={`${t('signUp')}`}
      // description={ 
      //   <>
      //     <p>
      //       {t('haveAccount')}
      //       <Link navigateTo={PATHS.LOGIN}>{t('login')}</Link>
      //     </p>
      //   </>
      // }
      form={form}
      submitText={`${t('signUp')}`}
      isLoading={isLoading}
      onSubmitHandler={onSubmitHandler}
      onErrorHandler={onErrorHandler}>
      <>
        {systemError && (
          <Flex ref={errorRef}>
            <Text color={'#a80000'}>{systemError}</Text>
          </Flex>
        )}
        <TextInput
          withAsterisk
          label={`${t('firstName')}`}
          placeholder={`${t('enterYour')} ${t('firstName').toLocaleLowerCase()}`}
          ariaLabel={`${t('nameDescribe', { name: 'first' })}`}
          {...form.getInputProps('firstName')}
        />
        <Space h={'md'} />
        <TextInput
          withAsterisk
          label={`${t('lastName')}`}
          placeholder={`${t('enterYour')} ${t('lastName').toLocaleLowerCase()}`}
          ariaLabel={`${t('nameDescribe', { name: 'last' })}`}
          {...form.getInputProps('lastName')}
        />
        <Space h={'md'} />
        <TextInput
          withAsterisk
          label={`${t('email')}`}
          placeholder={`${t('enterYour')} ${t('email')}`}
          ariaLabel={`${t('emailDescribe')}`}
          {...form.getInputProps('email')}
        />
        <Space h={'md'} />
        <PasswordInput
          withAsterisk
          label={t('password')}
          placeholder={t('createPassword')}
          ariaLabel={`${t('passwordDescribe')}`}
          autoComplete="new-password"
          {...form.getInputProps('password') }
        />
        <p>Password must contain at least 8 characters, one number, one lowercase letter, one uppercase letter, and one special character.</p>
        <Space h={'md'} />
        <PasswordInput
          withAsterisk
          label={`${t('confirmPassword')}`}
          placeholder={`${t('confirmPassword')}`}
          ariaLabel={`${t('confirmPasswordDescribe')}`}
          autoComplete="new-password"
          {...form.getInputProps('confirmPassword')}
        />
        <Space h={'md'} />
        <Checkbox
          error={form.errors.termsOfService}
          ariaLabel={`${t('termsDescribe')}`}
          checked={form.values.termsOfService}
          onChecked={(e) => form.setFieldValue('termsOfService', e.target.checked)}
          label={
            <span>
              {t('agree')}{' '}
              <Link targetBlank={true} navigateTo={'https://vially.io/services/terms-of-service'}>
                {t('termsOfUse')}
              </Link>{' '}
              &{' '}
              <Link targetBlank={true} navigateTo={'https://vially.io/privacy-policy'}>
                {t('privacy')}
              </Link>
              <span style={{ color: '#fa5252' }} aria-hidden>
                {' *'}
              </span>
            </span>
          }
        />
        <Space h={'md'} />
        {/* <Checkbox
          ariaLabel={`${t('enableIALabsNewsLetter')}`}
          checked={form.values.emailService}
          onChecked={(e) => form.setFieldValue('emailService', e.target.checked)}
          label={`${t('enableIALabsNewsLetter')}`}
        /> */}
      </>
    </AuthFormHoc>
  );
};

export default PersonalInfoForm;
